import UploadFileIcon from '@mui/icons-material/UploadFile'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import type { DocumentDto } from '@lawyer/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { ErrorAlert, LoadingBlock } from '../common/Feedback'
import { DocumentList } from './DocumentList'
import { FileDropZone } from './FileDropZone'
import { UploadDocumentDialog, type UploadScope } from './UploadDocumentDialog'

type Props = {
  scope: UploadScope
  emptyText?: string
  /** Document names worth a button of their own — the uploads made over and over. */
  quickUploads?: string[]
  /** Added to a shortcut's name, so downloaded files of different clients tell apart. */
  ownerName?: string
}

/** "דרכון - ישראל ישראלי.pdf": the extension too, or the download has none. */
function quickUploadName(label: string, ownerName: string | undefined, file: File) {
  const ext = /\.[^.]+$/.exec(file.name)?.[0] ?? ''
  return `${ownerName ? `${label} - ${ownerName}` : label}${ext}`
}

/** Documents for a single client or task. Cases use `CaseDocumentsPanel` instead. */
export function DocumentsPanel({ scope, emptyText, quickUploads = [], ownerName }: Props) {
  const queryClient = useQueryClient()
  /** Non-null while the upload dialog is open, carrying what the shortcut decided. */
  const [pending, setPending] = useState<{ name: string; file: File | null } | null>(null)

  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.documents(scope),
    queryFn: () => api.get<DocumentDto[]>('/documents', { ...scope }),
  })

  const refresh = () => {
    void refetch()
    void queryClient.invalidateQueries({ queryKey: ['case-documents'] })
  }

  return (
    <FileDropZone onFile={(file) => setPending({ name: '', file })}>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        {quickUploads.map((name) => (
          <Button key={name} component="label" startIcon={<UploadFileIcon />}>
            {`העלאת ${name}`}
            <input
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null
                // Cleared so picking the same file again still opens the dialog.
                e.target.value = ''
                if (file) setPending({ name: quickUploadName(name, ownerName, file), file })
              }}
            />
          </Button>
        ))}
        <Button
          startIcon={<UploadFileIcon />}
          onClick={() => setPending({ name: '', file: null })}
        >
          העלאת מסמך
        </Button>
      </Stack>

      {error ? <ErrorAlert error={error} onRetry={() => void refetch()} /> : null}
      {isPending ? <LoadingBlock /> : null}
      {data ? <DocumentList documents={data} emptyText={emptyText} onChanged={refresh} /> : null}

      <UploadDocumentDialog
        open={pending !== null}
        scope={scope}
        defaultName={pending?.name ?? ''}
        defaultFile={pending?.file ?? null}
        onClose={() => setPending(null)}
        onUploaded={refresh}
      />
    </FileDropZone>
  )
}
